import { useMemo } from 'react';
import { normalizePageUrl } from '../utils/referencePages';

/**
 * Moyenne neutre d'une liste de lignes sur un champ numérique (> 0 uniquement).
 * Ignore les valeurs nulles/0 et les lignes en chargement/erreur.
 */
export function averageScore(rows, getter) {
  const vals = (rows || [])
    .filter((r) => r && !r.loading && !r.error)
    .map(getter)
    .filter((v) => Number.isFinite(v) && v > 0);
  if (!vals.length) return null;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

/**
 * Scores moyens d'un projet, calculés une seule fois.
 *
 * Priorité aux scores persistés sur le projet (source de vérité backend), avec
 * repli sur une moyenne locale des lignes d'audit fournies. Utilisé par la
 * Synthèse, SEO, Performance et le rapport Pages de référence pour éviter de
 * recalculer la même moyenne dans chaque page.
 *
 * @param {object} project - objet projet (champs score_* persistés)
 * @param {{ seoRows?: any[], perfRows?: any[] }} options - lignes pour le repli local
 */
export default function useProjectScores(project, { seoRows, perfRows } = {}) {
  return useMemo(() => {
    const localSeo = averageScore(seoRows, (r) => Number(r.score_global));
    const refSet = new Set(
      (project?.reference_pages || []).map((u) => normalizePageUrl(u)).filter(Boolean),
    );
    const perfSource = refSet.size
      ? (perfRows || []).filter((a) => refSet.has(normalizePageUrl(a?.url)))
      : perfRows;
    const localPerf = averageScore(perfSource, (a) => Number(a.pagespeed_average));

    const seo = Math.round(Number(project?.score_seo ?? localSeo ?? 0)) || 0;
    const performance = Math.round(Number(project?.score_performance ?? localPerf ?? 0)) || 0;
    const technique = Math.round(Number(project?.score_technique ?? 0)) || 0;
    const global = Math.round(Number(project?.score_global ?? 0)) || 0;

    return { seo, performance, technique, global, localSeo, localPerf };
  }, [
    project?.score_seo,
    project?.score_performance,
    project?.score_technique,
    project?.score_global,
    seoRows,
    perfRows,
    project?.reference_pages,
  ]);
}
